import {
  EdgeDataKey,
  HierarchicalLayout,
  IMapper,
  LayoutBend,
  LayoutEdge,
  LayoutGraph,
  LayoutKeys,
  LayoutNode,
  LayoutStageBase,
  List,
  Point
} from '@yfiles/yfiles';

export enum NonHorizontalSegmentMovement {
  AUTOMATIC,
  ALWAYS_TOP,
  ALWAYS_BOTTOM
}

/**
 * Compacts octilinear edge routes produced the HierarchicalLayout by moving horizontal and diagonal
 * segments (in top-to-bottom layout orientation) to the top bottom.
 *
 * It considers edge thicknesses.
 *
 * Limitations:
 *  - The graph must not have backwards edges, i.e., edges from a layer with higher index to one
 *    with a lower index.
 *  - There must be a mapper already present for HierarchicalLayout.LAYER_INDEX_RESULT_DATA_KEY.
 *  - Edge groups must be defined. The class only looks at the target edge groups to determine
 *    which edges shall be grouped.
 *  - When using the automatic determination whether to move segments to the top or the bottom,
 *    the common segment of the grouped edges with the highest x-coordinate is selected.
 *    This plays together nicely with how the x-coordinates of the edges are determined by the
 *    custom ValueStreamCoordinateAssigner.
 */
export class CompactOctilinearRoutingStage extends LayoutStageBase {
  public static readonly NON_HORIZONTAL_SEGMENT_MOVEMENT_KEY: EdgeDataKey<NonHorizontalSegmentMovement> =
    new EdgeDataKey('NON_HORIZONTAL_SEGMENT_MOVEMENT_KEY', NonHorizontalSegmentMovement.AUTOMATIC);
  public edgeDistance: number = 10;
  public desiredOctilinearSegmentLength: number = 20;
  private readonly eps: number = 1e-9;

  protected applyLayoutImpl(graph: LayoutGraph): void {
    this.coreLayout?.applyLayout(graph);

    const layerIds = graph.context.getItemData(HierarchicalLayout.LAYER_INDEX_RESULT_DATA_KEY);
    const targetGroupIds = graph.context.getItemData(LayoutKeys.TARGET_EDGE_GROUP_ID_DATA_KEY);
    const thickness =
      graph.context.getItemData(HierarchicalLayout.EDGE_THICKNESS_DATA_KEY) ??
      IMapper.fromConstant<LayoutEdge, number>(0);
    const movementMapper =
      graph.context.getItemData(
        CompactOctilinearRoutingStage.NON_HORIZONTAL_SEGMENT_MOVEMENT_KEY
      ) ?? IMapper.fromConstant(NonHorizontalSegmentMovement.AUTOMATIC);

    if (!layerIds || !targetGroupIds) {
      // We only do something if layerIds and edge groups are defined.
      return;
    }

    this.shortenLongOctilinearLSegments(graph, thickness);
    const partialEdges = this.splitEdges(graph, layerIds, thickness);
    const groups = this.groupEdges(partialEdges, targetGroupIds);
    this.moveEdges(groups, movementMapper);
  }

  /**
   * Shortens the diagonal segments of all L-shapes to the desired length (considering thickness).
   */
  private shortenLongOctilinearLSegments(
    graph: LayoutGraph,
    thickness: IMapper<LayoutEdge, number>
  ): void {
    const thicknessFactor = Math.tan(Math.PI / 8);
    for (const edge of graph.edges) {
      const path = edge.pathPoints.toArray();
      const desiredLength =
        this.desiredOctilinearSegmentLength + thicknessFactor * (thickness.get(edge) ?? 0);
      for (let i = 1; i < path.length - 2; i++) {
        const currentLength = this.segmentLength(path, i);
        if (
          this.isVertical(path, i) ||
          this.isHorizontal(path, i) ||
          currentLength < desiredLength + this.eps
        ) {
          // The current segment is not diagonal
          continue;
        }

        let beforeVertical: boolean;
        if (this.isVertical(path, i - 1)) {
          beforeVertical = true;
        } else if (this.isHorizontal(path, i - 1)) {
          beforeVertical = false;
        } else {
          continue;
        }

        let afterVertical: boolean;
        if (this.isVertical(path, i + 1)) {
          afterVertical = true;
        } else if (this.isHorizontal(path, i + 1)) {
          afterVertical = false;
        } else {
          continue;
        }

        if (beforeVertical === afterVertical) {
          // We only shorten the diagonals of L-shapes
          continue;
        }

        const movement = Math.sqrt(0.5) * (currentLength - desiredLength);

        if (beforeVertical) {
          // move the y-coordinate of the first bend
          path[i].y += Math.sign(path[i].y - path[i - 1].y) * movement;
        } else {
          // move the x-coordinate of the first bend
          path[i].x += Math.sign(path[i].x - path[i - 1].x) * movement;
        }

        if (afterVertical) {
          // move the y-coordinate of the second bend
          path[i + 1].y += Math.sign(path[i + 1].y - path[i + 2].y) * movement;
        } else {
          // move the x-coordinate of the second bend
          path[i + 1].x += Math.sign(path[i + 1].x - path[i + 2].x) * movement;
        }
      }
    }
  }

  private calculateLayerDimensions(
    graph: LayoutGraph,
    layerIds: IMapper<LayoutNode, number>
  ): { min: number; max: number }[] {
    const layers = graph.nodes
      .filter((n) => layerIds.get(n) !== null)
      .groupBy((n) => layerIds.get(n)!)
      .toSorted(([i], [j]) => i - j)
      .toArray();
    return layers.map(([_, nodes]) => {
      const min = Math.min(...nodes.map((n) => n.layout.y));
      const max = Math.max(...nodes.map((n) => n.layout.y + n.layout.height));
      return { min, max };
    });
  }

  /**
   * Splits the edges into parts that lie completely between layer boundaries, i.e., either completely
   * within a layer or completely between two adjacent layers.
   */
  private splitEdges(
    graph: LayoutGraph,
    layerIds: IMapper<LayoutNode, number>,
    thickness: IMapper<LayoutEdge, number>
  ): PartialEdge[] {
    const layerDimensions = this.calculateLayerDimensions(graph, layerIds);
    return graph.edges
      .flatMap((e) =>
        List.fromArray(this.splitEdge(e, layerDimensions, layerIds, thickness.get(e) ?? 0))
      )
      .toArray();
  }

  private splitEdge(
    edge: LayoutEdge,
    layerDimensions: { min: number; max: number }[],
    layerIds: IMapper<LayoutNode, number>,
    thickness: number
  ): PartialEdge[] {
    let start = edge.sourcePortLocation;
    let currentLayer = layerIds.get(edge.source)!;
    let endLayer = layerIds.get(edge.target)!;
    let nextLayer = currentLayer + 1;
    const allBends = edge.bends.toArray();
    let bends: LayoutBend[] = [];
    let nextCoordinate = layerDimensions[nextLayer].min;
    const parts: PartialEdge[] = [];

    for (let i = 0; i < allBends.length; i++) {
      const bend = allBends[i];
      while (bend.y > nextCoordinate && nextLayer < endLayer) {
        // We reached the next layer or interlayer space
        if (bends.length > 0) {
          const end = bend.location;
          parts.push(new PartialEdge(edge, bends, start, end, currentLayer, nextLayer, thickness));
          bends = [];
        }
        if (currentLayer == nextLayer) {
          nextLayer++;
          nextCoordinate = layerDimensions[nextLayer].min;
        } else {
          currentLayer++;
          nextCoordinate = layerDimensions[nextLayer].max;
        }
      }
      bends.push(bend);
    }

    parts.push(
      new PartialEdge(
        edge,
        bends,
        start,
        edge.targetPortLocation,
        currentLayer,
        nextLayer,
        thickness
      )
    );

    return parts;
  }

  /**
   * Groups all partial edges first by layer, then by their edge group ID.
   */
  private groupEdges(edges: PartialEdge[], groupId: IMapper<LayoutEdge, any>): LayeredEdgeGroup[] {
    const key = (e: PartialEdge): number =>
      2 * e.sourceLayer + (e.sourceLayer == e.targetLayer ? 0 : 1);

    // Intermediate: interlayer space index -> (groupId -> edges)
    const buckets = new Map<number, Map<any, PartialEdge[]>>();

    for (const edge of edges) {
      const gId = groupId.get(edge.edge);
      const layerKey = key(edge);

      let groupMap = buckets.get(layerKey);
      if (!groupMap) {
        groupMap = new Map<any, PartialEdge[]>();
        buckets.set(layerKey, groupMap);
      }

      let edgeList = groupMap.get(gId);
      if (!edgeList) {
        edgeList = [];
        groupMap.set(gId, edgeList);
      }
      edgeList.push(edge);
    }

    // Build the result array, sorted by key
    const result: LayeredEdgeGroup[] = [];
    const sortedInterlayers = [...buckets.keys()].sort((a, b) => a - b);
    for (const i of sortedInterlayers) {
      const groupMap = buckets.get(i)!;
      const groups = [...groupMap.values()];
      const sourceLayer = groups[0][0].sourceLayer;
      const targetLayer = groups[0][0].targetLayer;
      result.push({ sourceLayer, targetLayer, groups });
    }

    return result;
  }

  private moveEdges(
    groups: LayeredEdgeGroup[],
    movementMapper: IMapper<LayoutEdge, NonHorizontalSegmentMovement>
  ): void {
    for (const interLayerGroup of groups) {
      const edgeParts: NonVerticalEdgePart[] = [];
      for (let j = 0; j < interLayerGroup.groups.length; j++) {
        const edges = interLayerGroup.groups[j];
        const paths = edges.map((e) => e.path);
        const middleIndices = this.findFirstOverlappingSegmentIndices(paths);
        for (let i = 0; i < edges.length; i++) {
          edgeParts.push(
            ...this.calculateNonVerticalEdgeParts(
              edges[i],
              middleIndices[i],
              movementMapper.get(edges[i].edge)!,
              j
            )
          );
        }
      }

      const topParts = edgeParts.filter((s) => s.top);
      this.applyMovements(topParts, 'top');

      const bottomParts = edgeParts.filter((s) => !s.top);
      this.applyMovements(bottomParts, 'bottom');
    }
  }

  /**
   * For each path, finds the index of the first segment that overlaps with the common vertical part shared by all paths.
   * Of all common vertical parts, the one with the largest x-coordinate is chosen.
   *
   * @returns Array of segment indices (one per path), or -1 if not found
   */
  private findFirstOverlappingSegmentIndices(paths: Point[][]): number[] {
    if (paths.length === 0) return [];

    // 1) For each path, collect its vertical segments as { x, yMin, yMax, index }.
    const verticalSegmentsPerPath = paths.map((path) => {
      const segments: { x: number; yMin: number; yMax: number; index: number }[] = [];
      for (let i = 0; i < path.length - 1; i++) {
        const a = path[i];
        const b = path[i + 1];
        if (a.x === b.x && a.y !== b.y) {
          segments.push({
            x: a.x,
            yMin: Math.min(a.y, b.y),
            yMax: Math.max(a.y, b.y),
            index: i
          });
        }
      }
      return segments;
    });

    // 2) Find the common vertical part with the largest x-coordinate.
    // Iterate candidates from the first path sorted by x descending.
    const candidates = [...verticalSegmentsPerPath[0]].sort((a, b) => b.x - a.x);

    let commonX: number | null = null;
    let commonYMin = -Infinity;
    let commonYMax = Infinity;

    for (const candidate of candidates) {
      let yMin = candidate.yMin;
      let yMax = candidate.yMax;
      let ok = true;

      for (let p = 1; p < verticalSegmentsPerPath.length; p++) {
        const match = verticalSegmentsPerPath[p].find((s) => s.x === candidate.x);
        if (!match) {
          ok = false;
          break;
        }
        yMin = Math.max(yMin, match.yMin);
        yMax = Math.min(yMax, match.yMax);
        if (yMin >= yMax) {
          ok = false;
          break;
        }
      }

      if (ok) {
        commonX = candidate.x;
        commonYMin = yMin;
        commonYMax = yMax;
        break; // largest-x candidate found
      }
    }

    if (commonX === null) {
      return paths.map(() => -1);
    }

    // 3) For each path, find the first segment that overlaps the common part.
    return verticalSegmentsPerPath.map((segments) => {
      const overlapping = segments.find(
        (s) => s.x === commonX && s.yMin < commonYMax && s.yMax > commonYMin
      );
      return overlapping ? overlapping.index : -1;
    });
  }

  private calculateNonVerticalEdgeParts(
    edge: PartialEdge,
    middleIndex: number,
    movement: NonHorizontalSegmentMovement,
    groupId: any
  ): NonVerticalEdgePart[] {
    const result: NonVerticalEdgePart[] = [];
    const bends = edge.bends;

    let start = 0;
    let current: NonVerticalEdgePart | undefined;
    for (let i = 0; i < bends.length - 1; i++) {
      if (this.isVertical(bends, i) && i > start) {
        current = this.createNonVerticalEdgePart(
          bends,
          start,
          i + 1,
          middleIndex,
          edge.thickness,
          movement,
          groupId
        );
        result.push(current);
        start = i + 1;
      }
    }

    if (bends.length > start) {
      result.push(
        this.createNonVerticalEdgePart(
          bends,
          start,
          bends.length,
          middleIndex,
          edge.thickness,
          movement,
          groupId
        )
      );
    }

    return result;
  }

  private createNonVerticalEdgePart(
    allBends: LayoutBend[],
    start: number,
    end: number,
    middleIndex: number,
    thickness: number,
    movement: NonHorizontalSegmentMovement,
    groupId: any
  ): NonVerticalEdgePart {
    const partBends = allBends.slice(start, end);
    const isTop =
      movement === NonHorizontalSegmentMovement.ALWAYS_TOP ||
      (movement === NonHorizontalSegmentMovement.AUTOMATIC && start < middleIndex);
    const [hasHorizontalSegment, y] = this.findEdgePartY(partBends, isTop);
    const minY = Math.min(...partBends.map((b) => b.y));
    const maxY = Math.max(...partBends.map((b) => b.y));
    return new NonVerticalEdgePart(
      partBends,
      y,
      minY,
      maxY,
      isTop,
      hasHorizontalSegment,
      thickness,
      groupId
    );
  }

  private findEdgePartY(partBends: LayoutBend[], isTop: boolean): [boolean, number] {
    for (let j = 0; j < partBends.length - 1; j++) {
      if (this.isHorizontal(partBends, j)) {
        return [true, partBends[j].y];
      }
    }
    return [false, isTop ? partBends[partBends.length - 1].y : partBends[0].y];
  }

  private isHorizontal(bends: { x: number; y: number }[], index: number): boolean {
    return Math.abs(bends[index].y - bends[index + 1].y) <= this.eps;
  }

  private isVertical(bends: { x: number; y: number }[], index: number): boolean {
    return Math.abs(bends[index].x - bends[index + 1].x) <= this.eps;
  }

  private segmentLength(path: { x: number; y: number }[], index: number): number {
    return Point.distance(path[index].x, path[index].y, path[index + 1].x, path[index + 1].y);
  }

  private applyMovements(edgeParts: NonVerticalEdgePart[], direction: 'top' | 'bottom'): void {
    const movements: Map<NonVerticalEdgePart, number> = new Map();
    edgeParts.sort((a, b) => a.y - b.y);
    this.calculateMovements(edgeParts, movements, direction);

    for (const part of edgeParts) {
      const movement = movements.get(part) ?? 0;
      for (const bend of part.bends) {
        bend.y += movement;
      }
    }
  }

  /**
   * Calculates by how much to move the edge parts such that they are tightly packed.
   * If two parts are already closer together than the edge distance, then this distance is kept.
   * Movements for all parts are calculated, but only parts that contain a horizontal segment
   * are relevant for the distance calculation.
   */
  private calculateMovements(
    edgeParts: NonVerticalEdgePart[],
    movements: Map<NonVerticalEdgePart, number>,
    direction: 'top' | 'bottom' = 'top'
  ): void {
    if (edgeParts.length === 0) {
      return;
    }

    const groupIdToLastSeen: Map<any, NonVerticalEdgePart> = new Map();

    if (direction === 'top') {
      let lastY = edgeParts[0].y;
      let lastMovement = 0;
      movements.set(edgeParts[0], 0);

      let movement: number;
      for (let i = 1; i < edgeParts.length; i++) {
        const part = edgeParts[i];
        if (part.startY <= lastY + this.edgeDistance) {
          movement = lastMovement;
        } else {
          movement = lastMovement - (part.startY - lastY - this.edgeDistance);
        }
        if (groupIdToLastSeen.has(part.groupId)) {
          // We must not get too close to the maximum of the previous part of this edge group
          const prev = groupIdToLastSeen.get(part.groupId)!;
          const prevMovement = movements.get(prev)!;
          const newDist = part.minY + movement - (prev.maxY + prevMovement);
          const oldDist = part.minY - prev.maxY;
          if (oldDist >= 0 && newDist < 0) {
            movement -= newDist;
          } else if (oldDist <= 0) {
            // they overlapped previously -> move them together
            movement = prevMovement;
          }
        }

        movements.set(part, movement);
        groupIdToLastSeen.set(part.groupId, part);
        if (part.hasHorizontalSegment) {
          lastMovement = movement;
          lastY = part.endY;
        }
      }
    } else {
      let lastY = edgeParts[edgeParts.length - 1].y;
      let lastMovement = 0;
      movements.set(edgeParts[edgeParts.length - 1], 0);

      let movement: number;
      for (let i = edgeParts.length - 2; i >= 0; i--) {
        const part = edgeParts[i];
        if (part.endY >= lastY - this.edgeDistance) {
          movement = lastMovement;
        } else {
          movement = lastMovement + (lastY - part.endY - this.edgeDistance);
        }
        if (groupIdToLastSeen.has(part.groupId)) {
          // We must not get too far from the minimum of the next part of this edge group
          const next = groupIdToLastSeen.get(part.groupId)!;
          const nextMovement = movements.get(next)!;
          const oldDist = next.minY - part.maxY;
          const newDist = next.minY + nextMovement - (part.maxY + movement);
          if (oldDist >= 0 && newDist < 0) {
            movement += newDist;
          } else if (oldDist <= 0) {
            // they overlapped previously -> move them together
            movement = nextMovement;
          }
        }

        movements.set(part, movement);
        groupIdToLastSeen.set(part.groupId, part);
        if (part.hasHorizontalSegment) {
          lastMovement = movement;
          lastY = part.startY;
        }
      }
    }
  }
}

class NonVerticalEdgePart {
  constructor(
    public bends: LayoutBend[],
    public y: number,
    public minY: number,
    public maxY: number,
    public top: boolean,
    public hasHorizontalSegment: boolean,
    public thickness: number,
    public groupId: any
  ) {}

  /**
   * Where the horizontal segment ends (including thickness).
   */
  public get endY(): number {
    return this.y + 0.5 * this.thickness;
  }

  /**
   * Where the horizontal segment starts (including thickness).
   */
  public get startY(): number {
    return this.y - 0.5 * this.thickness;
  }
}

type LayeredEdgeGroup = {
  sourceLayer: number;
  targetLayer: number;
  groups: PartialEdge[][];
};

/**
 * Part of an edge that does not cross layer boundaries.
 *
 * That is, it either lies completely within a layer or between two adjacent layers.
 */
class PartialEdge {
  public readonly edge: LayoutEdge;
  public readonly bends: LayoutBend[];
  public readonly sourcePosition: Point;
  public readonly targetPosition: Point;
  public readonly sourceLayer: number;
  public readonly targetLayer: number;
  public readonly thickness: number;

  public constructor(
    edge: LayoutEdge,
    bends: LayoutBend[],
    sourcePosition: Point,
    targetPosition: Point,
    sourceLayer: number,
    targetLayer: number,
    thickness: number
  ) {
    this.edge = edge;
    this.bends = bends;
    this.sourcePosition = sourcePosition;
    this.targetPosition = targetPosition;
    this.sourceLayer = sourceLayer;
    this.targetLayer = targetLayer;
    this.thickness = thickness;
  }

  public get path(): Point[] {
    return [this.sourcePosition, ...this.bends.map((b) => b.location), this.targetPosition];
  }
}
